import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Card, Chip, Typography } from "@rebasepro/ui";
import { useNavigate } from "react-router-dom";
import { collectionPath, inboxPath, lensWhere, summariseVisibility, type LinkFilter, type VisibilityRow, type VisibilitySummary } from "rebase-leadgen-config";
import { client } from "../lib/rebaseClient";

/**
 * The home page: what the pipeline did, and what it is waiting for.
 *
 * This exists because of a specific failure. The nightly crons ran, reported
 * "completed", and left every board empty — and there was no surface anywhere
 * that said *why*. The run summary was being written to `agent_runs` the whole
 * time, three rows a night, in a collection nobody thinks to open. A pipeline
 * whose stages are invisible is indistinguishable from a broken one.
 *
 * So the page shows where the work is piled up, and the last run of each job
 * WITH its notes and its per-source table. The notes are the diagnosis —
 * "GEMINI_API_KEY not set", "Reddit: 429" — and they render at full width
 * rather than truncated, because a truncated explanation is not one.
 *
 * ── Two intakes, one review habit ────────────────────────────────────────────
 * The counts are laid out as what they are rather than as one tidy funnel.
 * Signals and Companies are separate intakes that do not feed each other —
 * a signal is something a person said, a company is something a company
 * shipped — and drawing an arrow between them would be a lie about how the
 * work flows.
 *
 * ── On styling ───────────────────────────────────────────────────────────────
 * Everything here is `@rebasepro/ui` primitives and the admin's own Tailwind
 * tokens (`text-surface-*`, `bg-surface-accent-*`, `text-primary`), with a
 * `dark:` value wherever a light one is set. The first version of this file
 * hand-rolled its own palette — grey alphas, a bespoke type scale, a hardcoded
 * near-black that rendered invisible on the dark theme. It looked foreign
 * beside every other admin screen and broke the moment the theme changed. A
 * custom view is still an admin view; it should be built from the same parts.
 */

interface Counts {
    signals: number;
    signalsApproved: number;
    /** Rows waiting for review, not the whole board — most of it is the "not a fit" record. */
    companies: number;
    companiesApproved: number;
    people: number;
    peopleQueued: number;
    drafts: number;
    approved: number;
    sent: number;
    /** Citation targets nobody has triaged yet. */
    targets: number;
    /** Of those, the ones a model actually quoted — evidence, not a guess. */
    targetsCited: number;
}

interface RunRow {
    job?: string;
    success?: boolean;
    scanned?: number;
    kept?: number;
    duration_ms?: number;
    sources?: string;
    notes?: string;
    started_at?: string;
}

interface Delivery {
    configured: boolean;
    problems: string[];
    sentToday: number;
    dailyCap: number;
}

/**
 * `GET /api/functions/ops/status`. The half of "is this thing running" that the
 * dashboard could never see: whether the agent has any way to reach the person
 * who owns it.
 */
interface Channel {
    notifications: { configured: boolean; missing: string[]; note?: string };
}

/** Pending tasks due before tomorrow — overdue ones included. */
const TASKS_DUE = (): LinkFilter => {
    const tomorrow = new Date();
    tomorrow.setHours(24, 0, 0, 0);
    return { status: ["==", "pending"], due_date: ["<", tomorrow.toISOString()] };
};

const JOBS = [
    { id: "source-leads", label: "Source signals", when: "03:30 UTC" },
    { id: "auto-approve", label: "Promote and retire", when: "03:50 UTC" },
    { id: "enrich-prospects", label: "Find the people", when: "04:00 UTC" },
    { id: "draft-outreach", label: "Write the emails", when: "04:30 UTC" },
    { id: "discover-companies", label: "Find the companies", when: "05:00 UTC" },
    { id: "track-visibility", label: "Ask what an AI says", when: "Mon 06:00 UTC" },
    { id: "sync-inbox", label: "Read the mailboxes", when: "every 15 min" }
];

async function countWhere(collection: string, where?: Record<string, unknown>): Promise<number> {
    try {
        const { meta, data } = await client.data.collection(collection).find({
            ...(where ? { where } : {}),
            limit: 1,
            includeTotal: true
        } as never);
        const m = meta as { totalCount?: number; total?: number } | undefined;
        return m?.totalCount ?? m?.total ?? data.length;
    } catch {
        return 0;
    }
}

/**
 * Fetch the rows; `summariseVisibility` does the folding.
 *
 * The same function the morning brief calls, from the shared config package.
 * Two implementations of "group by run, tally the rivals" would diverge
 * silently, and the person reading both is the same person hours apart.
 *
 * Tolerant on purpose: this table is new, so an empty result is the normal
 * state rather than an error, and null lets the section say "no check has run
 * yet" instead of rendering a confident zero.
 */
async function loadVisibility(): Promise<VisibilitySummary | null> {
    try {
        const { data } = await client.data.collection("visibility_checks").find({
            sort: ["asked_at", "desc"],
            limit: 60
        } as never);
        return summariseVisibility((data ?? []) as VisibilityRow[]);
    } catch {
        return null;
    }
}

interface Stage {
    label: string;
    value: number;
    hint: string;
    /** The collection slug the count came from. */
    to: string;
    /**
     * The filter the count was made with, so the card opens THOSE rows.
     *
     * Not optional bookkeeping: "2 Drafts waiting on you" and "0 Approved" are
     * both counts of `emails`, and a card that opens the whole board makes the
     * reader find the two again by hand. Built from the same object the count
     * used, which is the only way the number and the destination cannot
     * disagree.
     */
    filter?: LinkFilter;
    /** Somewhere that is not a collection — the inbox, on the tab that counted. */
    path?: string;
}

function StageCards({ stages, arrows, loading }: { stages: Stage[]; arrows: boolean; loading: boolean }) {
    // The app is on a DATA router (`createBrowserRouter` in main.tsx), so its
    // URLs are paths. These cards used to navigate by assigning
    // `window.location.hash`, which on a path router appends `#/c/leads` to
    // whatever page you are on and does nothing else — every card on this
    // screen was inert, silently, and a card that does nothing reads as a
    // broken app rather than as a missing line of code.
    const navigate = useNavigate();
    return (
        <div className="flex flex-wrap items-stretch gap-2">
            {stages.map((stage, index) => (
                <React.Fragment key={stage.label}>
                    {arrows && index > 0 && (
                        <div className="self-center text-surface-400 dark:text-surface-500" aria-hidden="true">→</div>
                    )}
                    <Card
                        className="flex-1 min-w-[130px] p-4 flex flex-col gap-0.5 cursor-pointer"
                        onClick={() => navigate(stage.path ?? collectionPath(stage.to, stage.filter))}
                    >
                        <Typography variant="h5" className="mb-0 tabular-nums">
                            {loading ? "–" : stage.value}
                        </Typography>
                        <Typography variant="subtitle2" className="mb-0">{stage.label}</Typography>
                        <Typography variant="caption" color="secondary" className="mb-0">{stage.hint}</Typography>
                    </Card>
                </React.Fragment>
            ))}
        </div>
    );
}

export function PipelineHome() {
    const navigate = useNavigate();
    const [counts, setCounts] = useState<Counts | null>(null);
    const [inbox, setInbox] = useState<{ waiting: number; unread: number; today: number; tasksDue: number } | null>(null);
    const [runs, setRuns] = useState<Record<string, RunRow | undefined>>({});
    const [delivery, setDelivery] = useState<Delivery | null>(null);
    const [channel, setChannel] = useState<Channel | null>(null);
    const [visibility, setVisibility] = useState<VisibilitySummary | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [signals, signalsApproved, companies, companiesApproved, people, peopleQueued, drafts, approved, sent, targets, targetsCited] =
                await Promise.all([
                    countWhere("leads"),
                    countWhere("leads", { status: ["==", "approved"] }),
                    countWhere("companies", { status: ["==", "new"] }),
                    countWhere("companies", { status: ["==", "approved"] }),
                    countWhere("prospects"),
                    countWhere("prospects", { status: ["==", "ready"] }),
                    countWhere("emails", { status: ["==", "needs_review"] }),
                    countWhere("emails", { status: ["==", "approved"] }),
                    countWhere("emails", { status: ["==", "sent"] }),
                    countWhere("citation_targets", { status: ["==", "new"] }),
                    countWhere("citation_targets", { status: ["==", "new"], kind: ["==", "cited"] })
                ]);
            setCounts({ signals, signalsApproved, companies, companiesApproved, people, peopleQueued, drafts, approved, sent, targets, targetsCited });

            // The inbox, counted with the tabs' own definitions so each card
            // opens the tab that holds exactly that many.
            const [waiting, unread, today, tasksDue] = await Promise.all([
                countWhere("email_threads", lensWhere("waiting")),
                countWhere("email_threads", lensWhere("unread")),
                countWhere("email_threads", lensWhere("today")),
                countWhere("tasks", TASKS_DUE())
            ]);
            setInbox({ waiting, unread, today, tasksDue });

            setVisibility(await loadVisibility());

            // One query, newest first, then pick the newest row per job — four
            // filtered queries would cost four round trips to say the same thing.
            const { data } = await client.data.collection("agent_runs").find({
                sort: ["started_at", "desc"],
                limit: 40
            } as never);
            const latest: Record<string, RunRow> = {};
            for (const row of (data ?? []) as RunRow[]) {
                if (row.job && !latest[row.job]) latest[row.job] = row;
            }
            setRuns(latest);

            setDelivery(await client.functions.invoke<Delivery>("outreach", undefined, {
                method: "GET",
                path: "/status"
            }));

            // Separate call, and deliberately not folded into the one above:
            // "can we send to a prospect" and "can we reach Francesco" fail
            // independently, and for twenty-two days the second was false while
            // nothing on this page said so.
            try {
                setChannel(await client.functions.invoke<Channel>("ops", undefined, {
                    method: "GET",
                    path: "/status"
                }));
            } catch {
                setChannel(null);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { void load(); }, [load]);

    const conversations: Stage[] = inbox ? [
        { label: "Waiting on us", value: inbox.waiting, hint: "they asked; we have not answered", to: "email_threads", path: inboxPath({ lens: "waiting" }) },
        { label: "Unread", value: inbox.unread, hint: "new, not opened", to: "email_threads", path: inboxPath({ lens: "unread" }) },
        { label: "For today", value: inbox.today, hint: "set to look at again", to: "email_threads", path: inboxPath({ lens: "today" }) },
        { label: "Tasks due", value: inbox.tasksDue, hint: "today or overdue", to: "tasks", filter: TASKS_DUE() }
    ] : [];

    const intake: Stage[] = counts ? [
        { label: "Signals", value: counts.signals, hint: `${counts.signalsApproved} approved`, to: "leads" },
        { label: "Companies", value: counts.companies, hint: `${counts.companiesApproved} worth approaching`, to: "companies" }
    ] : [];

    const outreach: Stage[] = counts ? [
        { label: "People", value: counts.people, hint: `${counts.peopleQueued} queued to write`, to: "prospects" },
        {
            label: "Drafts",
            value: counts.drafts,
            hint: "waiting on you",
            to: "emails",
            filter: { status: ["==", "needs_review"] }
        },
        {
            label: "Approved",
            value: counts.approved,
            hint: "ready to send",
            to: "emails",
            filter: { status: ["==", "approved"] }
        },
        {
            label: "Sent",
            value: counts.sent,
            hint: "all time",
            to: "emails",
            filter: { status: ["==", "sent"] }
        }
    ] : [];

    /**
     * The one sentence that says what to do next.
     *
     * Ordered by what is most actionable, NOT by pipeline position. Walking the
     * stages in order got this wrong: a person leaves `ready` the moment their
     * email is drafted, so a board with drafts waiting reported "nobody
     * published an address" — true of the count, and useless as advice.
     */
    const nextStep = (): string => {
        if (!counts) return "";
        // Somebody waiting on an answer outranks everything the pipeline
        // produced overnight: they are a person, and they already wrote.
        if (inbox && inbox.waiting > 0) return `${inbox.waiting} conversation(s) are waiting on an answer. Open the inbox — they are on the first tab.`;
        if (counts.drafts > 0) return `${counts.drafts} draft(s) need reading. Check the "what it used" notes before approving anything.`;
        if (counts.approved > 0 && delivery && !delivery.configured) return `${counts.approved} email(s) are approved, but delivery is not configured — see below.`;
        if (counts.approved > 0) return `${counts.approved} email(s) are approved and ready. Send them one at a time from the Emails board.`;
        if (counts.signals === 0 && counts.companies === 0) return "Nothing on either board. Read the last run of each job below — the notes say what each source returned.";
        if (counts.signalsApproved === 0 && counts.signals > 0) return `${counts.signals} signal(s) on the board, none approved yet. The 03:50 run promotes anything the model scored 4 or more — a board that stays at zero means everything scored 3, or auto-approval is switched off.`;
        if (counts.companies > 0 && counts.companiesApproved === 0) return `${counts.companies} company(ies) are waiting for review. Each row says which backend they run and where that was found — approve the ones worth a first-hand look.`;
        // Above the people/prospect rungs: a page a model already quoted is a
        // standing opportunity, where an un-resolved prospect is usually just
        // somebody who published no address.
        if (counts.targetsCited > 0) return `${counts.targetsCited} page(s) an AI actually quoted about this category are unreviewed. Open Citation targets — each row says which question surfaced it.`;
        if (counts.people === 0) return "Signals are approved but nobody has been resolved yet. Run “Find the people” from Studio, or wait for 04:00 UTC.";
        if (counts.peopleQueued === 0) return "No one is queued to be written to. Either nobody published an address — the common case — or everyone ready has already been drafted.";
        return "There are people ready to write to. Run “Write the emails” from Studio, or wait for 04:30 UTC.";
    };

    return (
        <div className="p-8 max-w-5xl mx-auto flex flex-col gap-8">

            <div className="flex items-start justify-between gap-6">
                <div>
                    <Typography variant="h4" className="mb-1">Outreach</Typography>
                    <Typography variant="body2" color="secondary" className="max-w-[62ch]">
                        Two intakes — what people said in public, and what companies shipped — then the
                        person behind it, then an email you approve. Every stage runs unattended except
                        the ones you own.
                    </Typography>
                </div>
                <Button variant="outlined" size="small" onClick={() => void load()} disabled={loading}>
                    {loading ? "Loading…" : "Refresh"}
                </Button>
            </div>

            {error && <Alert color="error">Could not load: {error}</Alert>}

            <div className="flex flex-col gap-3">
                <div>
                    <Typography variant="subtitle2" className="mb-2">Inbox</Typography>
                    <StageCards stages={conversations} arrows={false} loading={loading && !inbox} />
                </div>
                <div>
                    <Typography variant="subtitle2" className="mb-2">Found for you</Typography>
                    <StageCards stages={intake} arrows={false} loading={loading && !counts} />
                </div>
                <div>
                    <Typography variant="subtitle2" className="mb-2">Waiting on you</Typography>
                    <StageCards stages={outreach} arrows loading={loading && !counts} />
                </div>
            </div>

            {/*
              Are we in the answer?

              Every band above this one counts things LEAVING the pipeline, and
              all of it is bounded by how many strangers one person can write to
              in a week. This is the only number here that measures demand.

              It is laid out around the rivals list rather than around our own
              score on purpose: early on `named` is zero every week, and a band
              that only says "0" teaches you to stop reading it. Who the model
              reaches for instead is the part you can act on.
            */}
            <div>
                <Typography variant="subtitle2" className="mb-2">Are we in the answer?</Typography>

                {!visibility && (
                    <Card className="p-4">
                        <Typography variant="body2" color="secondary" className="mb-0">
                            No visibility check has run yet. It fires Monday 06:00 UTC and needs
                            <code className="font-mono mx-1">GEMINI_API_KEY</code> — unlike every other job here it has
                            no keyless fallback, because the model is the thing being measured.
                        </Typography>
                    </Card>
                )}

                {visibility && (
                    <Card className="p-4 flex flex-col gap-3">
                        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                            <Typography variant="h5" className="mb-0 tabular-nums">
                                {visibility.named} of {visibility.asked}
                            </Typography>
                            <Typography variant="body2" color="secondary" className="mb-0">
                                buyer question(s) named rebase
                            </Typography>
                            {visibility.prevAsked > 0 && visibility.named !== visibility.prevNamed && (
                                <Chip
                                    size="small"
                                    colorScheme={visibility.named > visibility.prevNamed ? "greenLight" : "redLight"}
                                >
                                    {visibility.named > visibility.prevNamed ? "up" : "down"} from {visibility.prevNamed} of {visibility.prevAsked}
                                </Chip>
                            )}
                            <Typography variant="caption" color="secondary" className="mb-0 ml-auto tabular-nums">
                                {new Date(`${visibility.day}T00:00:00Z`).toLocaleDateString()}
                            </Typography>
                        </div>

                        {visibility.rivals.length > 0 && (
                            <div>
                                <Typography variant="caption" color="secondary" className="mb-1 uppercase tracking-wide">
                                    Named instead
                                </Typography>
                                <div className="flex flex-wrap gap-1.5">
                                    {visibility.rivals.map(rival => (
                                        <Chip key={rival.name} size="small">{rival.name} · {rival.count}</Chip>
                                    ))}
                                </div>
                            </div>
                        )}

                        {visibility.ungrounded > 0 && (
                            <Typography variant="caption" color="secondary" className="mb-0">
                                {visibility.ungrounded} answer(s) came back with no web sources — those reflect training
                                data, and nothing published this quarter will move them.
                            </Typography>
                        )}

                        <div className="flex flex-wrap gap-2">
                            <Button
                                variant="text"
                                size="small"
                                onClick={() => navigate(collectionPath("visibility_checks"))}
                            >
                                Read the answers
                            </Button>
                            <Button
                                variant="text"
                                size="small"
                                onClick={() => navigate(collectionPath("citation_targets", { status: ["==", "new"] }))}
                            >
                                {counts ? `${counts.targets} citation target(s)` : "Citation targets"}
                                {counts && counts.targetsCited > 0 ? ` · ${counts.targetsCited} quoted` : ""}
                            </Button>
                        </div>
                    </Card>
                )}
            </div>

            {counts && (
                <div className="rounded-lg border border-surface-200 dark:border-surface-700 bg-surface-accent-50 dark:bg-surface-800 px-4 py-3">
                    <Typography variant="body2" className="mb-0">
                        <b>Next:</b> {nextStep()}
                    </Typography>
                </div>
            )}

            <div>
                <Typography variant="subtitle1" className="mb-1">Last run of each job</Typography>
                <Typography variant="body2" color="secondary" className="mb-4 max-w-[70ch]">
                    The source table is the health check — a source reading <i>0 kept of 0 scanned</i> with
                    an error beside it has stopped working, whatever the run says overall. The notes are
                    the diagnosis.
                </Typography>

                <div className="grid gap-3 grid-cols-[repeat(auto-fit,minmax(280px,1fr))]">
                    {JOBS.map(job => {
                        const run = runs[job.id];
                        return (
                            <Card key={job.id} className="p-4 flex flex-col gap-3">
                                <div className="flex items-baseline justify-between gap-2">
                                    <Typography variant="subtitle2" className="mb-0">{job.label}</Typography>
                                    <Typography variant="caption" color="secondary" className="mb-0 tabular-nums">
                                        {job.when}
                                    </Typography>
                                </div>

                                {!run && (
                                    <Typography variant="body2" color="secondary" className="mb-0">
                                        Has never run, or its row was not recorded.
                                    </Typography>
                                )}

                                {run && (
                                    <>
                                        <div className="grid grid-cols-4 gap-2">
                                            {[
                                                ["Scanned", run.scanned ?? "–"],
                                                ["Kept", run.kept ?? "–"],
                                                ["Took", run.duration_ms != null ? `${(run.duration_ms / 1000).toFixed(1)}s` : "–"],
                                                ["When", run.started_at ? new Date(run.started_at).toLocaleDateString() : "–"]
                                            ].map(([label, value]) => (
                                                <div key={String(label)}>
                                                    <Typography variant="caption" color="secondary" className="mb-0 uppercase tracking-wide">
                                                        {label}
                                                    </Typography>
                                                    <Typography variant="body2" className="mb-0 font-medium tabular-nums">
                                                        {value}
                                                    </Typography>
                                                </div>
                                            ))}
                                        </div>

                                        {run.success === false && <Chip colorScheme="redLight" size="small">Reported a failure</Chip>}

                                        {run.sources && (
                                            <div>
                                                <Typography variant="caption" color="secondary" className="mb-1 uppercase tracking-wide">
                                                    Per source
                                                </Typography>
                                                <pre className="m-0 p-3 rounded-md whitespace-pre-wrap overflow-x-auto font-mono text-xs leading-relaxed bg-surface-accent-50 dark:bg-surface-800 text-surface-700 dark:text-surface-300">
                                                    {run.sources}
                                                </pre>
                                            </div>
                                        )}

                                        {run.notes && (
                                            <pre className="m-0 p-3 rounded-md whitespace-pre-wrap overflow-x-auto font-mono text-xs leading-relaxed bg-surface-accent-50 dark:bg-surface-800 text-surface-700 dark:text-surface-300">
                                                {run.notes}
                                            </pre>
                                        )}
                                    </>
                                )}
                            </Card>
                        );
                    })}
                </div>
            </div>

            <div>
                <Typography variant="subtitle1" className="mb-3">Reaching you</Typography>

                {channel?.notifications.configured && (
                    <Typography variant="body2" className="mb-0">
                        The morning brief has a channel. It goes out at 07:00 UTC.
                    </Typography>
                )}

                {channel && !channel.notifications.configured && (
                    <Alert color="error">
                        <Typography variant="body2" className="mb-2">
                            <b>The agent cannot reach you.</b> Every morning brief it has ever written
                            was recorded and delivered nowhere — which is why a full board can sit
                            untouched for weeks without anything appearing to be wrong.
                        </Typography>
                        <ul className="list-disc pl-5 mb-2">
                            {channel.notifications.missing.map(k => (
                                <li key={k}><Typography variant="body2" className="mb-0"><code className="font-mono">{k}</code></Typography></li>
                            ))}
                        </ul>
                        <Typography variant="caption" color="secondary" className="mb-0">
                            One value closes the loop: a Discord webhook URL, set with{" "}
                            <code className="font-mono">rebase cloud env set DISCORD_WEBHOOK_URL …</code>.
                            Until then this page is the only place the agent can tell you anything.
                        </Typography>
                    </Alert>
                )}
            </div>

            <div>
                <Typography variant="subtitle1" className="mb-3">Delivery</Typography>

                {!delivery && <Typography variant="body2" color="secondary">Checking…</Typography>}

                {delivery?.configured && (
                    <Typography variant="body2" className="mb-0">
                        Configured. {delivery.sentToday} of {delivery.dailyCap} sent today.
                    </Typography>
                )}

                {delivery && !delivery.configured && (
                    <Alert color="warning">
                        <Typography variant="body2" className="mb-2">
                            <b>Nothing can be sent yet</b> — the send endpoint answers 501 until these are set:
                        </Typography>
                        <ul className="list-disc pl-5 mb-2">
                            {delivery.problems.map(p => (
                                <li key={p}><Typography variant="body2" className="mb-0">{p}</Typography></li>
                            ))}
                        </ul>
                        <Typography variant="caption" color="secondary" className="mb-0">
                            Set them with <code className="font-mono">rebase cloud env set …</code>, then redeploy.
                            Drafting and review work without them.
                        </Typography>
                    </Alert>
                )}
            </div>
        </div>
    );
}

export default PipelineHome;
